import { createContext, useContext, useState, useEffect, ReactNode, FC, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface GlobalStateContextType {
  invalidateCache: (scope?: 'all' | 'user' | 'listings' | 'permissions') => Promise<void>;
  forceRefresh: () => void;
  isRefreshing: boolean;
}

const GlobalStateContext = createContext<GlobalStateContextType | undefined>(undefined);

interface GlobalStateProviderProps {
  children: ReactNode;
}

export const GlobalStateProvider: FC<GlobalStateProviderProps> = ({ children }) => {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);

  // Get user ID from Supabase directly instead of from useAuth to avoid circular dependency
  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => {
      setUserId(user?.id || null);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUserId(session?.user?.id || null);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Invalidate browser cache and local storage
  const invalidateCache = useCallback(async (scope: 'all' | 'user' | 'listings' | 'permissions' = 'all') => {
    setIsRefreshing(true);

    try {
      // Clear React Query cache for specific scopes
      if (scope === 'all' || scope === 'user') {
        // Trigger user data refetch
        localStorage.removeItem('user-mode');
      }

      if (scope === 'all' || scope === 'listings') {
        // Clear listing cache keys
        const keys = Object.keys(localStorage);
        keys.forEach(key => {
          if (key.includes('listing') || key.includes('booking')) {
            localStorage.removeItem(key);
          }
        });
      }

      // Invalidate Service Worker cache for PWA
      if ('serviceWorker' in navigator && 'caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(
          cacheNames.map(cacheName => {
            if (scope === 'all' || cacheName.includes('api') || cacheName.includes('data')) {
              return caches.delete(cacheName);
            }
          })
        );
      }

      // Small delay to allow cache clearing
      await new Promise(resolve => setTimeout(resolve, 100));
    } catch (error) {
      console.error('Cache invalidation error:', error);
    } finally {
      setIsRefreshing(false);
    }
  }, []);

  // Force complete page refresh
  const forceRefresh = useCallback(() => {
    window.location.reload();
  }, []);

  // Setup realtime listeners for critical tables
  useEffect(() => {
    if (!userId) return;

    const channels: any[] = [];

    // Create a stable reference to cache invalidation
    const handleProfileChange = async (payload: any) => {
      console.log('Profile changed:', payload);
      // Dispatch event only - let components handle their own cache
      window.dispatchEvent(new CustomEvent('profile-updated', { detail: payload }));
    };

    const handleMembershipChange = async (payload: any) => {
      console.log('Membership changed:', payload);
      window.dispatchEvent(new CustomEvent('membership-updated', { detail: payload }));
    };

    const handleListingsChange = async (payload: any) => {
      console.log('Listing changed:', payload);
      window.dispatchEvent(new CustomEvent('listings-updated', { detail: payload }));
    };

    // Listen to profile changes
    const profileChannel = supabase
      .channel('global-profile-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'profiles',
          filter: `user_id=eq.${userId}`
        },
        handleProfileChange
      )
      .subscribe();
    channels.push(profileChannel);

    // Listen to cooperative membership changes
    const membershipChannel = supabase
      .channel('global-membership-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'cooperative_memberships',
          filter: `user_id=eq.${userId}`
        },
        handleMembershipChange
      )
      .subscribe();
    channels.push(membershipChannel);

    // Listen to listing changes (for hosts)
    const listingsChannel = supabase
      .channel('global-listings-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'listings',
          filter: `user_id=eq.${userId}`
        },
        handleListingsChange
      )
      .subscribe();
    channels.push(listingsChannel);

    // Cleanup
    return () => {
      channels.forEach(channel => supabase.removeChannel(channel));
    };
  }, [userId]); // Only depend on userId

  return (
    <GlobalStateContext.Provider value={{ invalidateCache, forceRefresh, isRefreshing }}>
      {children}
    </GlobalStateContext.Provider>
  );
};

export const useGlobalState = () => {
  const context = useContext(GlobalStateContext);
  if (context === undefined) {
    throw new Error('useGlobalState must be used within a GlobalStateProvider');
  }
  return context;
};
